import classNames from 'classnames';
import type { MutableRefObject } from 'react';
import React, { useMemo } from 'react';
import { createPortal } from 'react-dom';

import type { ImMNElement } from '../../../../../common/elements/elementModelTypes';
import { type CellEditingSession, TableDataVersion } from '../../../../../common/table/TableTypes';
import type { TiptapContent } from '../../../../../common/tiptap/tiptapTypes';

import type { MilanoteEditorRef } from '../../../../components/editor/milanoteEditor/MilanoteEditorTypes';

import { getBackgroundColorClass } from '../../bridge/utils/dataUtils/cell/tableCellStyleUtils';
import { getTableCellVerticalAlignmentClassName } from '../utils/domUtils/tableCellVerticalAlignmentClassNameUtils';
import { getHotTableCellData } from '../../bridge/utils/hotInstanceUtils/meta/hotTableCellMetaUtils';
import type { MNHotInstance } from '../../bridge/tableHotInstanceTypes';
import DraftCellEditorComponent from './DraftCellEditorComponent';
import TiptapCellEditorComponent from './TiptapCellEditorComponent';
import useTableCellTextAlignment from './useTableCellTextAlignment';

import './MilanoteCellEditorComponent.scss';
import './MilanoteCellComponent.scss';

type MilanoteCellEditorComponentProps = {
    element: ImMNElement;
    elementId: string;
    isEditable: boolean;
    cellEditingSession: CellEditingSession | null;
    currentlyEditingEditorId: string | null;
    hotTableContainerRef: MutableRefObject<HTMLDivElement | null>;
    hotTableInstanceRef: MutableRefObject<MNHotInstance | null>;
    cellEditorRef: MutableRefObject<MilanoteEditorRef | null>;
    locale: string;
    gridSize: number;
    mounted: boolean;
    version: TableDataVersion;
    startEditingCellEditor: MNHotInstance['milanoteProps']['startEditingCellEditor'];
};

const MilanoteCellEditorComponent: React.FC<MilanoteCellEditorComponentProps> = ({
    element,
    elementId,
    isEditable,
    cellEditingSession,
    currentlyEditingEditorId,
    hotTableContainerRef,
    hotTableInstanceRef,
    cellEditorRef,
    locale,
    version,
    startEditingCellEditor,
}) => {
    const hotTableInstance = hotTableInstanceRef.current;
    const cellData =
        hotTableInstance && cellEditingSession
            ? getHotTableCellData(hotTableInstance, cellEditingSession.cellCoords)
            : undefined;

    const backgroundClasses = useMemo(() => getBackgroundColorClass(cellData), [cellData]);

    // TODO-TABLES: These can be inlined once draft is removed
    const tiptapCellEditingSession = cellEditingSession?.editorType === 'tiptap' ? cellEditingSession : null;
    const draftCellEditingSession = cellEditingSession?.editorType === 'draft' ? cellEditingSession : null;
    const tiptapHotTableInstanceRef = hotTableInstanceRef as MutableRefObject<MNHotInstance<
        TiptapContent,
        TiptapContent
    > | null>;
    const tiptapCellEditorRef = cellEditorRef as MutableRefObject<MilanoteEditorRef<TiptapContent> | null>;

    const { textAlignment, recomputeEditingTextAlignment } = useTableCellTextAlignment({
        cellData,
        cellEditingSession,
        locale,
    });

    const portalTarget = hotTableInstanceRef.current?.rootElement || hotTableContainerRef.current;

    if (!portalTarget) return null;

    return createPortal(
        <div className={classNames('MilanoteCellEditorComponent', backgroundClasses)}>
            <div className="MilanoteCellEditorComponent-relative">
                <div className="handsontable-input-positioner">
                    <div
                        className={classNames(
                            'handsontable-input-holder',
                            getTableCellVerticalAlignmentClassName(cellData?.verticalAlignment),
                        )}
                    >
                        {version === TableDataVersion.V3 ? (
                            <TiptapCellEditorComponent
                                // Update the key each session, to force the editor component to be re-created
                                key={cellEditingSession?.sessionId ?? 'idle'}
                                elementId={elementId}
                                isEditable={isEditable}
                                cellData={cellData}
                                cellEditingSession={tiptapCellEditingSession}
                                currentlyEditingEditorId={currentlyEditingEditorId}
                                hotTableInstanceRef={tiptapHotTableInstanceRef}
                                cellEditorRef={tiptapCellEditorRef}
                                textAlignment={textAlignment}
                                recomputeEditingTextAlignment={recomputeEditingTextAlignment}
                                startEditingCellEditor={startEditingCellEditor}
                            />
                        ) : (
                            <DraftCellEditorComponent
                                // Update the key each session, to force the editor component to be re-created
                                key={cellEditingSession?.sessionId ?? 'idle'}
                                element={element}
                                elementId={elementId}
                                isEditable={isEditable}
                                cellData={cellData}
                                cellEditingSession={draftCellEditingSession}
                                currentlyEditingEditorId={currentlyEditingEditorId}
                                hotTableInstanceRef={hotTableInstanceRef}
                                cellEditorRef={cellEditorRef}
                                locale={locale}
                                textAlignment={textAlignment}
                                recomputeEditingTextAlignment={recomputeEditingTextAlignment}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>,
        portalTarget,
    );
};

export default MilanoteCellEditorComponent;
